import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import GTranslateOutlinedIcon from "@mui/icons-material/GTranslateOutlined";
import PiCheckbox from "../../components/PiCheckbox";
import PiCreatorDropdown from "../../components/PiCreatorDropdown/PiCreatorDropdown";
import PiSegmentedToggle from "../../components/PiSegmentedToggle";
import {
  LanguageData,
  selectLocalizedEntries,
  selectLocalizedLanguages,
} from "../../app/slices/TrainingLocalizationSlice";
import { selectReferenceLanguage } from "../../app/slices/TrainingSlice";
import { countTextsToSend, TranslationScope } from "./translationScope";

export interface AdvancedTranslationSelection {
  from: string;
  to: string[];
  scope: TranslationScope;
}

/**
 * The advanced body of the Actions section. It replaces the two plain buttons
 * rather than sitting under them: with a narrowed scope on screen there must not
 * also be a button claiming to translate everything.
 */
export default function AdvancedTranslationPanel(props: {
  selection: AdvancedTranslationSelection;
  onChange: (selection: AdvancedTranslationSelection) => void;
  interactionKeys: string[];
  isTranslating: boolean;
  onTranslate: () => void;
}) {
  const {
    selection,
    onChange,
    interactionKeys,
    isTranslating,
    onTranslate,
  } = props;
  const { t } = useTranslation();
  const languages = useSelector(selectLocalizedLanguages);
  const entries = useSelector(selectLocalizedEntries);
  const referenceLanguage = useSelector(selectReferenceLanguage);

  const targets = useMemo(
    () => languages.filter((l) => l.shortcode !== selection.from),
    [languages, selection.from]
  );

  const totalTexts = useMemo(
    () =>
      countTextsToSend(
        entries,
        interactionKeys,
        selection.to,
        selection.scope
      ),
    [entries, interactionKeys, selection.to, selection.scope]
  );

  if (targets.length === 0) {
    return (
      <div className="px-1 py-2 text-[11px] leading-4 text-creator-text-sub">
        {t("interaction_translation.no_other_languages") as string}
      </div>
    );
  }

  const chooseSource = (shortcode: string) => {
    // The new source cannot also be a target, and the old one becomes eligible.
    const to = selection.to.filter((code) => code !== shortcode);
    onChange({ ...selection, from: shortcode, to });
  };

  const toggleTarget = (shortcode: string, checked: boolean) =>
    onChange({
      ...selection,
      to: checked
        ? [...selection.to, shortcode]
        : selection.to.filter((code) => code !== shortcode),
    });

  const sourceLanguage = languages.find((l) => l.shortcode === selection.from);

  /**
   * The column's chrome is square: 32px, 6px radius, hairline border — the same
   * shape as every other control here. PiCreatorDropdown's own `small` style is a
   * pill, so the trigger is supplied whole with triggerChrome="none".
   */
  const sourceTrigger = (
    <div className="w-full h-[32px] pl-2.5 pr-2 flex items-center gap-1.5 rounded-6 border border-creator-border bg-creator-surface">
      <span className="min-w-0 truncate text-[12px] font-medium text-creator-text">
        {sourceLanguage?.full ?? selection.from}
      </span>
      {selection.from === referenceLanguage && (
        <span className="flex-shrink-0 px-1.5 py-[1px] rounded-full bg-creator-primary-washed text-[8.5px] font-semibold uppercase tracking-wide text-creator-primary">
          {t("interaction_translation.reference") as string}
        </span>
      )}
      <span className="flex-1" />
      <ExpandMoreIcon
        className="flex-shrink-0 text-[16px] text-creator-text-sub"
        fontSize="inherit"
      />
    </div>
  );

  const sourceItems = languages.map((language: LanguageData) => (
    <div
      key={language.shortcode}
      className="py-[6px] text-[13px] text-creator-text"
      onClick={() => chooseSource(language.shortcode)}
      data-testid={`translate-from-option-${language.shortcode}`}
    >
      {language.full}
    </div>
  ));

  const actionLabel = t(
    selection.scope === "missing"
      ? "interaction_translation.translate_texts"
      : "interaction_translation.overwrite_texts",
    { count: totalTexts }
  ) as string;

  // One style for all three field labels. They were drifting apart — uppercase
  // soft for the first, sentence-case sub for the other two — which is most of
  // why the panel read as an undifferentiated stack rather than three groups.
  const fieldLabel = "text-[10px] font-semibold uppercase tracking-wide text-creator-text-soft";
  const quietLink =
    "bg-transparent border-0 p-0 cursor-pointer text-[10px] font-semibold text-creator-primary";

  return (
    // gap-3 between groups against gap-1.5 inside them: a label has to sit
    // closer to the control it names than the groups do to each other.
    <div className="flex flex-col gap-3 px-1 py-2">
      <div className="flex flex-col gap-1.5">
        <span className={fieldLabel}>
          {t("interaction_translation.translate_from") as string}
        </span>
        <PiCreatorDropdown
          trigger={sourceTrigger}
          items={sourceItems}
          triggerChrome="none"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-center gap-2">
          <span className={`flex-1 ${fieldLabel}`}>
            {t("interaction_translation.translate_to") as string}
          </span>
          <button
            type="button"
            className={quietLink}
            onClick={() =>
              onChange({
                ...selection,
                to: targets.map((l) => l.shortcode),
              })
            }
          >
            {t("interaction_translation.select_all") as string}
          </button>
          <span className="text-[10px] text-creator-text-soft">·</span>
          <button
            type="button"
            className={quietLink}
            onClick={() => onChange({ ...selection, to: [] })}
          >
            {t("interaction_translation.select_none") as string}
          </button>
        </div>

        <div className="flex flex-col gap-0.5">
          {targets.map((language) => (
            <div
              key={language.shortcode}
              className="flex items-center gap-2 px-1 py-1 rounded-5"
              data-testid={`translate-to-${language.shortcode}`}
            >
              <PiCheckbox
                size="sm"
                checked={selection.to.includes(language.shortcode)}
                onToggle={(next) => toggleTarget(language.shortcode, next)}
                testId={`translate-to-toggle-${language.shortcode}`}
              />
              <span className="flex-1 min-w-0 truncate text-[12px] text-creator-text">
                {language.full}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <span className={fieldLabel}>
          {t("interaction_translation.what_to_translate") as string}
        </span>
        <PiSegmentedToggle<TranslationScope>
          variant="square"
          dense
          fullWidth
          value={selection.scope}
          onChange={(scope) => onChange({ ...selection, scope })}
          options={[
            {
              value: "missing",
              label: t("interaction_translation.scope_missing") as string,
              testId: "scope-missing",
            },
            {
              value: "all",
              label: t("interaction_translation.scope_all") as string,
              testId: "scope-all",
            },
          ]}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <button
          type="button"
          className="w-full h-[34px] inline-flex items-center justify-center gap-1.5 rounded-6 border-0 bg-creator-primary hover:bg-creator-primary-hover text-white text-[12.5px] font-semibold cursor-pointer disabled:bg-creator-primary-disabled disabled:cursor-not-allowed"
          disabled={
            isTranslating || selection.to.length === 0 || totalTexts === 0
          }
          onClick={onTranslate}
          data-testid="advanced-translate-button"
        >
          <GTranslateOutlinedIcon fontSize="inherit" />
          {actionLabel}
        </button>

        {selection.to.length > 0 && totalTexts === 0 && (
          <span className="text-[10px] text-creator-text-soft">
            {t("interaction_translation.nothing_to_do") as string}
          </span>
        )}
      </div>
    </div>
  );
}
